import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { PLAN_KEYS, PLANS } from '@magig/schemas';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PageEyebrow } from '../components/auth-shells.js';
import { BrandSparkle, Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { getActiveBrandId } from '../lib/active-brand.js';
import { trackConversion } from '../lib/analytics.js';
import {
  type BillingPlanDisplay,
  type BillingState,
  createCheckoutSessionApi,
  type PlanKey,
} from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { formatNumber } from '../lib/format.js';
import { featuresFor, planBlurbs, universalTerms } from '../lib/plan-copy.js';
import { billingQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { PENDING_PLAN_STORAGE_KEY } from './start.js';

// Plan-selection / paywall step. Value-first reorder (§12.9): this now
// sits at the END of a brand's onboarding — the founder reaches it from
// the competition step, AFTER seeing the brand brain + competitive
// playbook the free preview produced. Jobs:
//
//   1. Show the two plans with expanded long-form copy so the founder
//      makes an informed pick before paying.
//   2. Pre-select the plan they came in with from the landing page
//      (stored in sessionStorage by /start, read once here, cleared
//      immediately so a refresh doesn't re-prompt).
//   3. On continue, open Stripe Checkout with returnTo pointed at the
//      just-onboarded brand's home (`/brand/<slug>/home`).
//   4. On the post-checkout return (?checkout=success), show a
//      "finalizing" state that polls billing until the subscription
//      webhook lands, then forwards to the brand home — covering the
//      webhook-vs-redirect race the hard gate would otherwise lose.
//
// Skipped entirely if the org already has an active/trialing
// subscription (beforeLoad redirects straight to the brand home) — e.g.
// a Growth user onboarding a 2nd brand, or a stale-link/browser-back.
//
// Chrome matches the other onboarding steps (BrandSparkle corner +
// wordmark + Mag mascot + PageEyebrow), NOT WorkspaceNav — onboarding
// is a focused single-purpose flow; tab nav doesn't belong here.

interface PlanSearch {
  /** Slug of the brand whose onboarding just finished — set by the
   *  competition step and the brand-layout paywall gate. Drives where
   *  checkout returns to (`/brand/<slug>/home`). */
  brand?: string;
  /** `success` once Stripe bounces back here after checkout. The page
   *  shows a "finalizing" state and polls billing until the webhook
   *  flips the subscription, then forwards to the brand home. */
  checkout?: string;
  /** Why the founder is here EARLY. Normally they arrive having finished
   *  onboarding; `out_of_tokens` means the free signup credit ran out
   *  mid-flow and a step could not complete. Without this the picker
   *  greets them with "welcome · pick your plan" as though nothing went
   *  wrong, and they are left to work out why mag stopped. */
  reason?: string;
}

interface PlanLoaderData {
  me: unknown;
  billing: BillingState;
  /** Resolved target brand slug for post-checkout return, or null when
   *  the founder somehow reached the picker with no brand yet. */
  targetSlug: string | null;
  /** True when we arrived via a Stripe checkout-success redirect. */
  finalizing: boolean;
}

export const Route = createFileRoute('/onboarding/plan')({
  validateSearch: (search: Record<string, unknown>): PlanSearch => {
    const out: PlanSearch = {};
    if (search['reason'] === 'out_of_tokens') out.reason = 'out_of_tokens';
    if (typeof search['brand'] === 'string' && search['brand'].length > 0) {
      out.brand = search['brand'];
    }
    if (search['checkout'] === 'success') out.checkout = 'success';
    return out;
  },
  beforeLoad: async ({ context: { queryClient }, search }): Promise<PlanLoaderData> => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });

    const finalizing = search.checkout === 'success';
    // Post-checkout the subscription flips via a Stripe webhook, which
    // can lag the browser redirect by a beat. Force a fresh read so we
    // don't show the picker again to someone who just paid (billingQuery
    // has a 30s staleTime that would otherwise serve the stale 'none').
    if (finalizing) {
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    }
    const billing = await queryClient.ensureQueryData(billingQuery());

    // Resolve which brand to return to after payment: explicit ?brand
    // wins, then the active brand, then the first live brand.
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const live = brands.filter((b) => b.archivedAt === null);
    const activeId = getActiveBrandId();
    const target =
      (search.brand ? live.find((b) => b.slug === search.brand) : undefined) ??
      live.find((b) => b.id === activeId) ??
      live[0] ??
      null;

    // Already subscribed → no paywall. Land them on the brand home (or
    // /home if they have no brand yet, which routes them onward).
    if (billing.subscriptionStatus === 'active' || billing.subscriptionStatus === 'trialing') {
      if (target) throw redirect({ to: '/brand/$slug/home', params: { slug: target.slug } });
      throw redirect({ to: '/home' });
    }

    return { me, billing, targetSlug: target?.slug ?? null, finalizing };
  },
  loader: ({ context }) => context as PlanLoaderData,
  component: PlanOnboardingPage,
});

function PlanOnboardingPage() {
  const { billing, targetSlug, finalizing } = Route.useLoaderData();
  const navigate = useNavigate();

  // Post-checkout webhook-lag handling. beforeLoad already forced one
  // fresh billing read; if the subscription still hasn't flipped we land
  // here in "finalizing" mode and poll until it does, then forward to
  // the brand home. Capped so a webhook that never arrives degrades to a
  // manual escape hatch rather than spinning forever.
  const { data: livePolledBilling } = useQuery({
    ...billingQuery(),
    refetchInterval: finalizing ? 2_000 : false,
    enabled: finalizing,
  });
  const subscribedNow =
    livePolledBilling?.subscriptionStatus === 'active' ||
    livePolledBilling?.subscriptionStatus === 'trialing';
  // Narrowed to a primitive so the effect below can depend on the plan
  // without taking the whole polled object as a dependency.
  const subscribedPlan = subscribedNow ? (livePolledBilling?.plan ?? null) : null;
  // The paid conversion is reported HERE, not when Checkout opened and not
  // when Stripe redirected back. This is the first line that runs after the
  // webhook has actually flipped the subscription to active/trialing, which
  // is the only moment we know money moved. Ref-guarded because the poll
  // re-renders and StrictMode double-mounts in dev; a second event would
  // double-count the revenue.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (!finalizing || !subscribedNow) return;
    if (!reportedRef.current) {
      reportedRef.current = true;
      trackConversion('subscription_started', {
        plan: subscribedPlan,
        // Mag weights findings by value, so the monthly price is what makes
        // this comparable to a token top-up rather than just another count.
        value: subscribedPlan ? PLANS[subscribedPlan].basePriceUsd : null,
        // Whether they already finished a brand's onboarding before paying —
        // the value-first reorder means most should have, and the ones who
        // haven't came down a different path.
        with_brand: targetSlug !== null,
      });
    }
    if (targetSlug) {
      void navigate({ to: '/brand/$slug/home', params: { slug: targetSlug } });
    } else {
      void navigate({ to: '/home' });
    }
  }, [finalizing, subscribedNow, subscribedPlan, targetSlug, navigate]);

  if (finalizing) {
    return <FinalizingScreen targetSlug={targetSlug} />;
  }
  return <PlanPicker billing={billing} targetSlug={targetSlug} />;
}

function PlanPicker({ billing, targetSlug }: { billing: BillingState; targetSlug: string | null }) {
  // Arrived EARLY because the free signup credit ran out mid-onboarding,
  // rather than by finishing it. See the note on PlanSearch.reason.
  const outOfTokens = Route.useSearch().reason === 'out_of_tokens';
  const [pendingPlan, setPendingPlan] = useState<PlanKey | null>(null);

  // Read the deep-linked plan from sessionStorage once (set by /start
  // when the founder came in from the landing's pricing card). Clear
  // it on read so a refresh doesn't keep highlighting the same option
  // after they've decided otherwise.
  const preselected = useMemo<PlanKey | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.sessionStorage.getItem(PENDING_PLAN_STORAGE_KEY);
      // Validated against PLAN_KEYS — a literal pair went stale the
      // moment the ladder grew past two rungs.
      if (stored !== null && (PLAN_KEYS as readonly string[]).includes(stored)) {
        window.sessionStorage.removeItem(PENDING_PLAN_STORAGE_KEY);
        return stored as PlanKey;
      }
    } catch {
      // sessionStorage can throw in private browsing — silent fallback.
    }
    return null;
  }, []);

  // Selected = explicit user click; falls back to the deep-linked pick
  // so the "continue" button is always actionable.
  const [selected, setSelected] = useState<PlanKey>(preselected ?? 'founder_pack');

  const handleContinue = async () => {
    setPendingPlan(selected);
    try {
      // Value-first reorder: the founder has already been through the
      // brand's onboarding, so send Stripe-success straight to that
      // brand's home (not /settings/billing — they're here to start
      // using Mag). On the rare no-brand path, fall back to /home, which
      // routes them onward. The brand-layout gate + this page's
      // finalizing poll cover the webhook-lag window on return.
      const returnTo = targetSlug ? `/brand/${targetSlug}/home` : '/home';
      const { url } = await createCheckoutSessionApi(selected, { returnTo });
      window.location.href = url;
    } catch (err) {
      magToast.error("couldn't open checkout.", founderErrorCopy(err));
      setPendingPlan(null);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[var(--cream)] px-4 py-12 sm:px-6">
      {/* Top-left corner glyph + wordmark — same decoration as the
          other onboarding steps' CenteredShell so this page reads as
          part of the same flow. */}
      <BrandSparkle
        size={320}
        color="var(--spell-soft)"
        style={{
          position: 'absolute',
          top: -70,
          left: -90,
          opacity: 0.55,
          pointerEvents: 'none',
        }}
      />
      <header className="absolute left-8 top-8 z-10">
        <Link
          to="/home"
          className="font-display text-2xl font-black tracking-[-0.045em] text-[var(--ink)] no-underline"
        >
          magig
        </Link>
      </header>

      {/* Wider than CenteredShell (720px vs 520px) — the two stacked
          plan cards need more breathing room than a single-input
          onboarding step. min-h-[80vh] + centered keeps the page
          balanced even when the cards collapse on mobile. */}
      {/* 1080px so the four rungs fit side by side, matching the picker in
          settings and the landing grid. It was 720px, which was right while
          the cards stacked — but xl:grid-cols-4 below is a VIEWPORT
          breakpoint, not a container one, so leaving the cap at 720 would
          have crammed four cards into 720px on a wide screen. */}
      <section className="relative mx-auto flex min-h-[80vh] max-w-[1080px] flex-col items-center justify-center gap-5 text-center">
        <Mag size={86} mood="casting" />
        <PageEyebrow>
          {outOfTokens ? (
            <Trans>that's the free credit used up</Trans>
          ) : (
            <Trans>welcome · pick your plan</Trans>
          )}
        </PageEyebrow>
        <h1 className="font-display text-[40px] sm:text-[56px] font-black leading-[1] tracking-[-0.045em]">
          {outOfTokens ? (
            <Trans>pick a plan and i'll pick up where i left off.</Trans>
          ) : (
            <Trans>which mag do you need?</Trans>
          )}
        </h1>
        {/* Two-plan copy lived here under four cards, same as the picker
            in settings did: "on both" named a ladder with two rungs, and
            "growth just unlocks more brands" is no longer what separates
            them — in_house costs more than founder_pack while granting the
            SAME one brand, because what it buys is mag acting without
            asking. Says the same thing the modal now says, in mag's voice:
            the rung changes her reach and her leash, not the product. */}
        <p className="max-w-[560px] font-serif text-xl leading-tight text-[var(--ink)]/80">
          <Trans>
            “same me on every rung. what changes is how many brands i run, how much i can do without
            asking you, and how much work is included. switch any time.”
          </Trans>
        </p>

        <div className="mt-4 grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {billing.plans.map((plan) => (
            <OnboardingPlanCard
              key={plan.key}
              plan={plan}
              selected={selected === plan.key}
              preselected={preselected === plan.key}
              onSelect={() => setSelected(plan.key)}
              disabled={pendingPlan !== null}
            />
          ))}
        </div>

        {/* The same shared-terms row the picker in settings uses. It is here
            and not only there BECAUSE THIS IS THE PAYWALL: "cancel any time,
            no contract" is the sentence a founder most wants at the moment
            they are asked to pay, and lifting it out of the cards would
            otherwise have deleted it from the one screen where it matters
            most. Said once, for every rung. */}
        <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[12px] text-[var(--ink)]/70">
          {universalTerms().map((term) => (
            <li key={term} className="flex items-center gap-1.5">
              <span aria-hidden className="text-[var(--spell)]">
                {'•'}
              </span>
              {term}
            </li>
          ))}
        </ul>

        <div className="mt-4 flex w-full justify-end">
          <button
            type="button"
            onClick={handleContinue}
            disabled={pendingPlan !== null}
            className="btn btn-lg disabled:opacity-60"
          >
            {pendingPlan ? (
              <Trans>opening checkout…</Trans>
            ) : (
              <Trans>continue with {labelFor(selected, billing)}</Trans>
            )}
            <span className="btn-arrow">→</span>
          </button>
        </div>

        <p className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--ink)]/55">
          <Trans>stripe handles the payment · cancel any time</Trans>
        </p>
      </section>
    </main>
  );
}

function labelFor(key: PlanKey, billing: BillingState): string {
  return billing.plans.find((p) => p.key === key)?.label ?? key;
}

// Shown after a successful Stripe checkout while we wait for the
// subscription webhook to land (PlanOnboardingPage polls billing and
// redirects the instant it flips). After a grace window we surface a
// manual continue link so a delayed/missed webhook never traps the
// founder on a spinner — they paid; they should always be able to move.
function FinalizingScreen({ targetSlug }: { targetSlug: string | null }) {
  const [tookTooLong, setTookTooLong] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setTookTooLong(true), 12_000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center gap-5 bg-[var(--cream)] px-4 py-12 text-center sm:px-6">
      <BrandSparkle
        size={320}
        color="var(--spell-soft)"
        style={{ position: 'absolute', top: -70, left: -90, opacity: 0.55, pointerEvents: 'none' }}
      />
      <div className="mag-breathe relative">
        <Mag size={86} mood="casting" />
      </div>
      <PageEyebrow>
        <Trans>payment received</Trans>
      </PageEyebrow>
      <h1 className="font-display text-[32px] sm:text-[44px] font-black leading-[1] tracking-[-0.04em]">
        <Trans>wiring up your workspace…</Trans>
      </h1>
      <p className="max-w-[460px] font-serif text-xl leading-tight text-[var(--ink)]/80">
        <Trans>“thanks — that's locked in. give me a second to flip everything on.”</Trans>
      </p>
      {tookTooLong ? (
        <div className="mt-2 flex flex-col items-center gap-2">
          <p className="mag-voice text-[13.5px] leading-[1.5] text-[var(--ink)]/55">
            <Trans>taking a touch longer than usual. you can head in — it'll catch up.</Trans>
          </p>
          {targetSlug ? (
            <Link
              to="/brand/$slug/home"
              params={{ slug: targetSlug }}
              className="btn"
              reloadDocument
            >
              <Trans>go to your home</Trans>
              <span className="btn-arrow">→</span>
            </Link>
          ) : (
            <Link to="/home" className="btn" reloadDocument>
              <Trans>go to your home</Trans>
              <span className="btn-arrow">→</span>
            </Link>
          )}
        </div>
      ) : null}
    </main>
  );
}

function formatCount(n: number): string {
  return formatNumber(n);
}

function OnboardingPlanCard({
  plan,
  selected,
  preselected,
  onSelect,
  disabled,
}: {
  plan: BillingPlanDisplay;
  selected: boolean;
  preselected: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  const blurb = planBlurbs()[plan.key];
  const features = featuresFor(plan);

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      // Cards are click-to-select (radio-style). Selected state gets
      // the ink border + plum shadow that everything else in the
      // dashboard uses for "active". Left-aligned text — even though
      // the surrounding shell is centered, card content reads better
      // flush-left.
      className={`flex flex-col rounded-2xl border bg-white p-5 text-left transition disabled:opacity-60 ${
        selected
          ? 'border-[1.5px] border-[var(--ink)] shadow-[4px_4px_0_var(--plum)]'
          : 'border-[var(--line)] hover:border-[var(--ink)]/40'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          ↘ {plan.label}
        </p>
        {selected ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--spell)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]">
            <Trans>selected</Trans>
          </span>
        ) : preselected ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--paper)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]/65">
            <Trans>you picked this</Trans>
          </span>
        ) : null}
      </div>
      <p className="mt-2 font-display text-[32px] font-black leading-none tracking-[-0.02em] text-[var(--ink)]">
        ${plan.basePriceUsd}
        <span className="ml-1 font-mono text-[12px] font-normal text-[var(--ink)]/55">
          <Trans>/mo</Trans>
        </span>
      </p>
      <p className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {blurb.tagline}
      </p>
      <p className="mt-3 text-[13px] leading-[1.55] text-[var(--ink)]/75">{blurb.description}</p>
      <ul className="mt-4 flex flex-col gap-1.5 text-[13px] text-[var(--ink)]/85">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--spell)]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}
